import { useDispatch, useStore } from "react-redux";
import useCommonApi from "../api/useCommonApi";
import { ApplicationState } from "../redux/reducers";
import {
    setGuiaTarifaError,
    setGuiaTarifaLoading,
    setGuiaTarifaSuccess
} from "../redux/actions/guiaTarifaActions";
import { buildGuiaRecalcularTarifaRequest } from "../components/servicioAdicional/buildGuiaRecalcularTarifaRequest";
import { IGuiaTarifa } from "../entities/guias/IGuiaTarifa";
import { ServicioAdicional } from "../entities/catalogos/ServicioAdicional";

type RecalcOptions = {
    serviciosE3?: ServicioAdicional[];
};

const mapResultado = (result: any): Omit<IGuiaTarifa, 'status' | 'errorMessage' | 'dirty'> => ({
    mercancias: result?.mercancias ?? [],
    desgloseFlete: result?.desgloseFlete ?? [],
    desgloseSeguro: result?.desgloseSeguro ?? [],
    desgloseRad: result?.desgloseRad ?? [],
    desgloseEad: result?.desgloseEad ?? [],
    desgloseAjuste: result?.desgloseAjuste ?? [],
    desgloseCruce: result?.cruce ?? [],
    conceptos: result?.conceptos ?? [],
    combustible: result?.combustible ?? [],
    totales: result?.totales
        ? {
            importe: result.totales.importe,
            subtotal: result.totales.subtotal,
            iva: result.totales.iva,
            retencion: result.totales.retencion,
            total: result.totales.total,
            cantidadTotal: result.totales.cantidadTotal,
            ivaSinAcc: result.totales.ivaSinAcc,
            pesoFacturado: result.totales.pesoFacturado,
            peso: result.totales.peso,
            volumen: result.totales.volumen,
            descuento: result.totales.descuento ?? null,
            descuentoMsj: result.totales.descuentoMsj ?? null,
            cobroEnDestino: result.totales.cobroEnDestino ?? null,
            paridad: result.totales.paridad ?? null
        }
        : null
});

const useRecalcularTarifaGuia = () => {
    const api = useCommonApi();
    const dispatch = useDispatch();
    const store = useStore<ApplicationState>();

    const recalcular = async (options: RecalcOptions = {}): Promise<{ ok: boolean; errorMessage?: string }> => {
        const state = store.getState();
        const guiaWizard = state.guiaWizard;
        const guiaOrigen = state.guiaOrigen;
        const guiaDestino = state.guiaDestino;
        const guiaMercancias = state.guiaMercancias;
        const guiaServicios = options.serviciosE3 ?? state.guiaServicios;

        if (!guiaWizard?.tipoServicioId) {
            const msg = "Tipo de servicio no resuelto.";
            dispatch(setGuiaTarifaError(msg));
            return { ok: false, errorMessage: msg };
        }
        if (!guiaWizard?.tipoEnvioId) {
            const msg = "Tipo de envío no resuelto.";
            dispatch(setGuiaTarifaError(msg));
            return { ok: false, errorMessage: msg };
        }
        if (!guiaOrigen?.sucursal?.id || !guiaDestino?.sucursal?.id) {
            const msg = "Origen/destino incompletos para recalcular.";
            dispatch(setGuiaTarifaError(msg));
            return { ok: false, errorMessage: msg };
        }
        if (!guiaMercancias?.some(m => (m.cantidad || 0) > 0 && (m.embalajeId || 0) > 0)) {
            const msg = "Se requiere al menos una mercancía válida.";
            dispatch(setGuiaTarifaError(msg));
            return { ok: false, errorMessage: msg };
        }

        const request = buildGuiaRecalcularTarifaRequest({
            guiaWizard,
            guiaOrigen,
            guiaDestino,
            guiaMercancias,
            guiaServicios,
            idioma: state.idioma?.idioma || localStorage.getItem("idioma") || "es-MX"
        });

        dispatch(setGuiaTarifaLoading());
        try {
            const result = await api.postAsync<any>("guia/recalcular-tarifa", request);
            if (!result?.totales) {
                const msg = "El motor tarifario no devolvió totales.";
                dispatch(setGuiaTarifaError(msg));
                return { ok: false, errorMessage: msg };
            }
            dispatch(setGuiaTarifaSuccess(mapResultado(result)));
            return { ok: true };
        } catch (e) {
            const msg =
                e?.response?.data?.error?.message
                || e?.message
                || "Error al recalcular tarifa";
            dispatch(setGuiaTarifaError(msg));
            return { ok: false, errorMessage: msg };
        }
    };

    return { recalcular };
};

export default useRecalcularTarifaGuia;
